import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';

import { useDashboardContext } from '../dashboard/DashboardContext';
import useIsPrinting from '../../../utilities/PrintUtilities';
import { getLogger } from '../../../utilities/UseLog';

const log = getLogger('FilterControl');

const FilterControlContext = createContext();

export function useFilterControlContextValue() {
    const isPrinting = useIsPrinting();
    const { editing } = useDashboardContext();
    const [autoRefreshPauseCount, setAutoRefreshPauseCount] = useState(0);
    const pauseAutoRefresh = useCallback(
        () =>
            setAutoRefreshPauseCount((currentCount) => {
                const newCount = isPrinting ? currentCount : currentCount + 1;

                log.debug(`pauseAutoRefresh: pause count ${newCount}`);

                return newCount;
            }),
        [isPrinting]
    );
    const resumeAutoRefresh = useCallback(
        () =>
            setAutoRefreshPauseCount((currentCount) => {
                const newCount = currentCount === 0 ? currentCount : currentCount - 1;

                log.debug(`resumeAutoRefresh: pause count ${newCount}`);

                return newCount;
            }),
        []
    );

    const autoRefreshPaused = useMemo(
        () => isPrinting || editing || autoRefreshPauseCount > 0,
        [autoRefreshPauseCount, editing, isPrinting]
    );

    return useMemo(
        () => ({
            autoRefreshPaused,
            pauseAutoRefresh,
            resumeAutoRefresh,
        }),
        [autoRefreshPaused, pauseAutoRefresh, resumeAutoRefresh]
    );
}

export function useFilterControl() {
    const paused = useRef(false);
    const filterControl = useContext(FilterControlContext);

    return useMemo(
        () => ({
            ...filterControl,
            // This prevents callers from having mismatched pause/resume calls, which can leave auto-refresh permanently paused.
            pauseAutoRefresh: () => {
                if (paused.current === true) return;
                filterControl.pauseAutoRefresh();
                paused.current = true;
            },
            resumeAutoRefresh: () => {
                if (paused.current !== true) return;
                filterControl.resumeAutoRefresh();
                paused.current = false;
            },
        }),
        [filterControl]
    );
}

export default FilterControlContext;
